import { useLazyQuery } from "@apollo/client";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { fetchAllPrelemSearchList } from "@platformx/authoring-apis";
import { Loader, NoSearchResult } from "@platformx/utilities";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import PrelemListItem, { type PrelemItem } from "./PrelemListItem";

const PAGE_SIZE = 10;

const useStyles = makeStyles(() =>
  createStyles({
    rotateIcon: {
      animation: "$spin 5s linear infinite",
    },
    "@keyframes spin": {
      "0%": {
        transform: "rotate(360deg)",
      },
      "100%": {
        transform: "rotate(0deg)",
      },
    },
    container: {
      padding: "20px 24px",
      margin: "0 auto",
    },
    scrollLoader: {
      display: "flex",
      justifyContent: "center",
      padding: "20px 0",
    },
  }),
);

function PrelemsList() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [loader, setLoader] = useState(true);
  const [prelemList, setPrelemList] = useState<PrelemItem[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isSpinning, setIsSpinning] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [fetchPrelemSearchList] = useLazyQuery(fetchAllPrelemSearchList);

  const fetchPrelems = useCallback(async () => {
    try {
      setLoader(true);
      const res = await fetchPrelemSearchList({
        variables: {
          obj: { start: 0, rows: 100 },
          sort: "DESC",
          searchText: "",
          tag: [""],
          layout: [""],
        },
      });
      if (res?.data?.authoring_prelemSearch) {
        const { prelems } = res.data.authoring_prelemSearch;
        const list: PrelemItem[] = Array.isArray(prelems) ? prelems : [];
        setPrelemList(list);
      } else {
        setPrelemList([]);
      }
    } catch (err) {
      console.error("Error fetching prelems list", err);
      setPrelemList([]);
    } finally {
      setLoader(false);
      setIsSpinning(false);
    }
  }, [fetchPrelemSearchList]);

  useEffect(() => {
    fetchPrelems();
  }, [fetchPrelems]);

  const handleRefresh = () => {
    setIsSpinning(true);
    setVisibleCount(PAGE_SIZE);
    setRefreshKey((prev) => prev + 1);
    fetchPrelems();
  };

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, prelemList.length));
  };

  if (loader) {
    return <Loader />;
  }

  if (prelemList.length === 0) {
    return <NoSearchResult />;
  }

  const visiblePrelems = prelemList.slice(0, visibleCount);
  const hasMore = visibleCount < prelemList.length;

  return (
    <div className={classes.container}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: { xs: "16px", sm: "20px", md: "24px" },
        }}>
        <Typography variant='h3semibold' sx={{ textTransform: "capitalize" }}>
          {t("Prelems List")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            onClick={handleRefresh}
            data-testid='prelems-list-refresh'
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}>
            <SyncIcon className={isSpinning ? classes.rotateIcon : ""} />
          </Box>
        </Box>
      </Box>
      <InfiniteScroll
        dataLength={visiblePrelems.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box className={classes.scrollLoader}>
            <CircularProgress size={28} />
          </Box>
        }
        endMessage={
          <Typography variant='body2' align='center' sx={{ padding: "16px 0", color: "#999" }}>
            {t("All prelems loaded")}
          </Typography>
        }>
        {visiblePrelems.map((prelem) => (
          <PrelemListItem key={prelem.PrelemId} prelem={prelem} refreshKey={refreshKey} />
        ))}
      </InfiniteScroll>
    </div>
  );
}
export default PrelemsList;
